import React from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Home, ChevronRight, Plus, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import ProjectPageForm from '@/features/project-pages/components/ProjectPageForm'
import { useCreateProjectPage } from '@/features/project-pages/hooks/useProjectPages'
import type { ProjectPageFormValues } from '@/lib/validation/projectPage'
import { toProjectPageFormData } from '@/features/project-pages/utils/toProjectPageFormData'

/**
 * Project Page Create Page
 *
 * Provides interface for creating new project pages.
 */
export default function ProjectPageCreate() {
  const navigate = useNavigate()
  const createMutation = useCreateProjectPage()

  const handleSubmit = async (data: ProjectPageFormValues) => {
    try {
      await createMutation.mutateAsync(toProjectPageFormData(data))
      navigate('/content/project-pages')
    } catch (error) {
      // Error toast already shown by mutation
      console.error('Failed to create project page:', error)
    }
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground transition-colors">
          <Home className="h-4 w-4" />
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to="/content/project-pages" className="hover:text-foreground transition-colors">
          Project Pages
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-foreground font-medium">Create New</span>
      </nav>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Plus className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">Create Project Page</h1>
          </div>
          <p className="text-muted-foreground">
            Create a new marketing page for a completed masonry project
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/content/project-pages')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to List
        </Button>
      </div>

      {/* Preview Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          After creating this project page, you'll be able to preview how it will appear on fixmybrick.ca by editing the page and switching to the Preview tab.
        </AlertDescription>
      </Alert>

      {/* Form */}
      <ProjectPageForm
        onSubmit={handleSubmit}
        isSubmitting={createMutation.isPending}
      />
    </div>
  )
}
